class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.upsellItems = [];
    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    this.initializeUpsellItems();
    this.moveModalsToBody();
    this.checkCartForUpsellItems().then(() => {
      // After checking cart, add items by default if needed
      this.handleAddByDefault();
    });
    
    // Subscribe to cart updates to sync checkbox states
    if (typeof subscribe !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Only update attributes if the update came from cart-upsell or on initial check
        // This prevents clearing attributes when other items are added/removed
        const shouldUpdateAttributes = event.source === 'cart-upsell';
        this.checkCartForUpsellItems(shouldUpdateAttributes);
      });
    }
  }

  moveModalsToBody() {
    // Move all upsell modals to document.body for proper styling
    const modals = this.querySelectorAll('modal-dialog.phi--upsell-product-modal');
    modals.forEach((modal) => {
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
      
      // Override the hide method to preserve overflow-hidden
      if (modal.hide && !modal._hideOverridden) {
        modal.hide = function() {
          // NEVER remove overflow-hidden - cart drawer or other elements may need it
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
          this.removeAttribute('open');
          if (this.openedBy) {
            removeTrapFocus(this.openedBy);
          }
          window.pauseAllMedia();
        };
        modal._hideOverridden = true;
      }
      
      // Override the show method to not add overflow-hidden
      if (modal.show && !modal._showOverridden) {
        modal.show = function(opener) {
          this.openedBy = opener;
          const popup = this.querySelector('.template-popup');
          // Don't add overflow-hidden - cart drawer already handles it
          this.setAttribute('open', '');
          if (popup) popup.loadContent();
          trapFocus(this, this.querySelector('[role="dialog"]'));
          window.pauseAllMedia();
        };
        modal._showOverridden = true;
      }
      
      // Set up close button handlers (ModalDialog already handles click, but we override hide)
      const closeButton = modal.querySelector('[id^="ModalClose-"]');
      if (closeButton) {
        // Override the default hide behavior if needed
        // ModalDialog constructor already adds the click handler
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initializeUpsellItems() {
    const items = this.querySelectorAll('.cart-upsell-item');
    items.forEach((item) => {
      const variantId = item.dataset.variantId;
      const title = item.dataset.title;
      const tag = item.dataset.tag || '';
      const addByDefault = item.dataset.addByDefault === 'true';
      
      if (variantId) {
        // Debug: log tag value to verify it's being read correctly
        if (tag) {
          console.log(`Cart upsell: Initialized item with variant ${variantId}, tag: "${tag}"`);
        }
        
        this.upsellItems.push({
          element: item,
          variantId: parseInt(variantId),
          title: title,
          tag: tag,
          addByDefault: addByDefault
        });

        const checkbox = item.querySelector('.cart-upsell-checkbox');
        if (checkbox) {
          // Update accessibility attributes on initialization
          this.updateCheckboxAccessibility(checkbox, false);
          
          checkbox.addEventListener('change', (e) => {
            this.updateCheckboxAccessibility(checkbox, e.target.checked);
            this.handleCheckboxChange(e, variantId, title);
          });
        }

        // Handle info icon click to open modal
        const infoIcon = item.querySelector('.cart-upsell-info-icon');
        if (infoIcon) {
          const modalId = infoIcon.dataset.modalTrigger;
          if (modalId) {
            infoIcon.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.openUpsellModal(modalId, infoIcon);
            });
          }
        }
      }
    });
  }

  async checkCartForUpsellItems(updateAttributes = true) {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cart = await response.json();
      
      this.upsellItems.forEach((item) => {
        const checkbox = item.element.querySelector('.cart-upsell-checkbox');
        if (checkbox) {
          const isInCart = cart.items.some(
            (cartItem) => cartItem.variant_id === item.variantId
          );
          checkbox.checked = isInCart;
          // Update accessibility attributes when cart state changes
          this.updateCheckboxAccessibility(checkbox, isInCart);
        }
      });

      // Only update cart attributes if requested (when upsell items are added/removed, or on initial load)
      if (updateAttributes) {
        await this.updateCartAttributes(cart);
        
        // Debug: Log all upsell items and their tags
        console.log('Cart upsell: All initialized items:', this.upsellItems.map(item => ({
          variantId: item.variantId,
          title: item.title,
          tag: item.tag
        })));
      }
      return cart;
    } catch (error) {
      console.error('Error checking cart:', error);
      return null;
    }
  }

  async handleAddByDefault() {
    // Wait a bit to ensure cart check is complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    this.upsellItems.forEach((item) => {
      if (item.addByDefault) {
        const checkbox = item.element.querySelector('.cart-upsell-checkbox');
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
          this.addToCart(item.variantId, item.title);
        }
      }
    });
  }

  updateCheckboxAccessibility(checkbox, isChecked) {
    const productTitle = checkbox.dataset.productTitle || '';
    const checkedState = isChecked ? 'true' : 'false';
    const action = isChecked ? 'Exclude' : 'Include';
    const toggleState = isChecked ? 'on' : 'off';
    const ariaLabel = `${action} ${productTitle}, Toggle is ${toggleState}`;
    const title = `${action} ${productTitle}, Toggle is ${toggleState}`;
    
    checkbox.setAttribute('aria-checked', checkedState);
    checkbox.setAttribute('aria-label', ariaLabel);
    checkbox.setAttribute('title', title);
  }

  openUpsellModal(modalId, opener) {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    
    // Ensure modal is in body
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    
    // Override hide/show methods if not already done
    if (modal.hide && !modal._hideOverridden) {
      modal.hide = function() {
        // NEVER remove overflow-hidden - cart drawer or other elements may need it
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        this.removeAttribute('open');
        if (this.openedBy) {
          removeTrapFocus(this.openedBy);
        }
        window.pauseAllMedia();
      };
      modal._hideOverridden = true;
    }
    
    if (modal.show && !modal._showOverridden) {
      const self = this;
      modal.show = function(opener) {
        this.openedBy = opener;
        const popup = this.querySelector('.template-popup');
        // Don't add overflow-hidden - cart drawer already handles it
        this.setAttribute('open', '');
        if (popup) popup.loadContent();
        trapFocus(this, this.querySelector('[role="dialog"]'));
        window.pauseAllMedia();
        // Initialize features after modal opens
        setTimeout(() => {
          self.initializeUpsellModalFeatures(this);
        }, 100);
      };
      modal._showOverridden = true;
    }
    
    if (modal && typeof modal.show === 'function') {
      modal.show(opener);
    } else {
      // Fallback if modal-dialog custom element not available
      // Don't add overflow-hidden - cart drawer already handles it
      modal.setAttribute('open', '');
      
      // Ensure close button has handler (ModalDialog handles it, but we need to override hide)
      const closeButton = modal.querySelector('[id^="ModalClose-"]');
      if (closeButton) {
        closeButton.focus();
      }
      
      // Close on backdrop click
      const backdropHandler = (e) => {
        if (e.target === modal) {
          this.closeUpsellModal(modalId);
        }
      };
      modal.addEventListener('click', backdropHandler);
      modal._backdropHandler = backdropHandler;
      
      // Close on escape key
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          this.closeUpsellModal(modalId);
        }
      };
      document.addEventListener('keydown', escapeHandler);
      modal._escapeHandler = escapeHandler;
    }
    
    // Initialize slider and collapsible after modal opens
    // Use setTimeout to ensure modal is fully rendered
    setTimeout(() => {
      this.initializeUpsellModalFeatures(modal);
    }, 100);
    
    // Also observe for attribute changes in case modal is opened via show() method
    if (!modal._observerAttached) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (modal.hasAttribute('open')) {
              // Modal just opened, initialize features
              setTimeout(() => {
                this.initializeUpsellModalFeatures(modal);
              }, 100);
            }
          }
        });
      });
      
      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['open']
      });
      
      modal._observerAttached = true;
    }
  }
  
  initializeUpsellModalFeatures(modal) {
    // Theme's slider-component custom element will auto-initialize when added to DOM
    // Ensure slider component has proper structure before initialization
    
    // Initialize collapsible description
    this.initializeUpsellCollapsible(modal);
  }
  
  initializeUpsellCollapsible(modal) {
    const toggle = modal.querySelector('[data-description-toggle]');
    const content = modal.querySelector('[data-description-content]');
    
    if (!toggle || !content) return;
    
    // Remove existing event listener by cloning
    const newToggle = toggle.cloneNode(true);
    toggle.parentNode.replaceChild(newToggle, toggle);
    
    // Set initial state (expanded)
    newToggle.setAttribute('aria-expanded', 'true');
    if (content) {
      content.classList.add('phi--upsell-modal__description--expanded');
      content.classList.remove('phi--upsell-modal__description--collapsed');
    }
    
    // Add click handler
    newToggle.addEventListener('click', () => {
      const isExpanded = newToggle.getAttribute('aria-expanded') === 'true';
      const newExpanded = !isExpanded;
      
      newToggle.setAttribute('aria-expanded', newExpanded.toString());
      
      if (content) {
        if (newExpanded) {
          content.classList.add('phi--upsell-modal__description--expanded');
          content.classList.remove('phi--upsell-modal__description--collapsed');
        } else {
          content.classList.remove('phi--upsell-modal__description--expanded');
          content.classList.add('phi--upsell-modal__description--collapsed');
        }
      }
    });
  }

  closeUpsellModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    
    if (modal && typeof modal.hide === 'function') {
      modal.hide();
    } else {
      // Don't remove overflow-hidden - cart drawer or other elements may need it
      modal.removeAttribute('open');
      
      // Remove event listeners
      if (modal._backdropHandler) {
        modal.removeEventListener('click', modal._backdropHandler);
        delete modal._backdropHandler;
      }
      if (modal._escapeHandler) {
        document.removeEventListener('keydown', modal._escapeHandler);
        delete modal._escapeHandler;
      }
    }
  }

  async handleCheckboxChange(event, variantId, title) {
    const checkbox = event.target;
    
    if (checkbox.checked) {
      await this.addToCart(variantId, title);
    } else {
      await this.removeFromCart(variantId, title);
    }
  }

  async addToCart(variantId, title) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      
      // Parse response text first to handle both success and error cases
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        // If response is not valid JSON, handle as error
        console.error('Error adding to cart: Invalid response from server');
        const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId));
        if (item) {
          const checkbox = item.element.querySelector('.cart-upsell-checkbox');
          if (checkbox) checkbox.checked = false;
        }
        return;
      }
      
      // Check if response indicates an error
      if (!response.ok || data.status) {
        const errorMessage = data.description || data.message || 'Failed to add item to cart';
        console.error('Error adding to cart:', errorMessage);
        // Revert checkbox state on error
        const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId));
        if (item) {
          const checkbox = item.element.querySelector('.cart-upsell-checkbox');
          if (checkbox) checkbox.checked = false;
        }
        return;
      }

      // Wait a bit for cart to update, then update cart attributes with tags
      setTimeout(async () => {
        await this.updateCartAttributesFromCart();
      }, 300);

      // Publish cart update event
      if (typeof publish !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upsell',
          cartData: data,
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Revert checkbox state on error
      const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId));
      if (item) {
        const checkbox = item.element.querySelector('.cart-upsell-checkbox');
        if (checkbox) checkbox.checked = false;
      }
    }
  }

  async removeFromCart(variantId, title) {
    try {
      // Get current cart to find line item
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      if (!cartResponse.ok) {
        console.error('Failed to fetch cart:', cartResponse.status);
        return;
      }
      const cart = await cartResponse.json();
      
      // Find the item in the cart by variant_id
      // Convert variantId to number for comparison since cart items use numbers
      const variantIdNum = parseInt(variantId);
      const itemIndex = cart.items.findIndex((item) => item.variant_id === variantIdNum || item.variant_id === variantId);
      
      if (itemIndex === -1) {
        // Item not in cart, just update attributes to ensure they're cleared
        await this.updateCartAttributesFromCart();
        return;
      }

      const cartItem = cart.items[itemIndex];
      
      // Line numbers in Shopify cart API are 1-based
      // itemIndex is the 0-based array index, so we add 1 to get the line number
      const lineNumber = itemIndex + 1;
      
      // Get sections to render if we're in a cart drawer
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const sections = cartDrawerItems ? ['cart-drawer'] : [];
      
      const body = JSON.stringify({
        line: lineNumber,
        quantity: 0,
        sections: sections,
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        ...{ body }
      });

      // Parse response text first to handle both success and error cases
      const responseText = await response.text();
      let parsedData;
      
      try {
        parsedData = JSON.parse(responseText);
      } catch (e) {
        // If response is not valid JSON, handle as error
        console.error('Error removing from cart: Invalid response from server');
        const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId));
        if (item) {
          const checkbox = item.element.querySelector('.cart-upsell-checkbox');
          if (checkbox) checkbox.checked = true;
        }
        return;
      }

      // Check if response indicates an error
      if (!response.ok) {
        const errorMessage = parsedData?.description || parsedData?.message || `Server error: ${response.status} ${response.statusText}`;
        console.error('Error removing from cart:', errorMessage, parsedData);
        // Revert checkbox state on error
        const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId));
        if (item) {
          const checkbox = item.element.querySelector('.cart-upsell-checkbox');
          if (checkbox) checkbox.checked = true;
        }
        return;
      }

      // Check for errors in the parsed data
      if (parsedData.errors) {
        console.error('Error removing from cart:', parsedData.errors);
        // Revert checkbox state on error
        const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId));
        if (item) {
          const checkbox = item.element.querySelector('.cart-upsell-checkbox');
          if (checkbox) checkbox.checked = true;
        }
        return;
      }

      // Update cart attributes after removal - wait for cart to fully update
      // Use a longer delay to ensure the cart has been updated on the server
      setTimeout(async () => {
        await this.updateCartAttributesFromCart();
      }, 500);

      // Publish cart update event to refresh cart drawer
      if (typeof publish !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upsell',
          cartData: parsedData,
        });
      }
    } catch (error) {
      console.error('Error removing from cart:', error, error.stack);
      // Revert checkbox state on error
      const item = this.upsellItems.find((i) => i.variantId === parseInt(variantId) || i.variantId === variantId);
      if (item) {
        const checkbox = item.element.querySelector('.cart-upsell-checkbox');
        if (checkbox) checkbox.checked = true;
      }
    }
  }

  async updateCartAttributes(cart) {
    const orderTags = [];
    
    // Check which upsell items are currently in the cart and collect their tags
    this.upsellItems.forEach((item) => {
      const isInCart = cart.items.some(
        (cartItem) => cartItem.variant_id === item.variantId
      );
      if (isInCart && item.tag) {
        // Only add tag if it's not empty and not already in the array
        const trimmedTag = item.tag.trim();
        if (trimmedTag && !orderTags.includes(trimmedTag)) {
          orderTags.push(trimmedTag);
        }
      }
    });

    // Update cart attributes with order tags
    // NOTE: Cart attributes appear as "Additional details" in orders, not as order tags.
    // To convert these to actual order tags, you need to set up Shopify Flow:
    // 1. Create a Flow with trigger: "Order created"
    // 2. Add condition: Check if order attribute "Add-on Items" is not empty
    // 3. Add action: "Add order tags" - use the value from order attribute "Add-on Items"
    //    (Note: For multiple tags, Flow may need to parse comma-separated values)
    const attributes = {};
    if (orderTags.length > 0) {
      // Store tags as comma-separated string in cart attribute
      // This format works well with Shopify Flow to extract and add as order tags
      attributes['Add-on Items'] = orderTags.join(', ');
      console.log('Cart upsell: Updating cart attributes with tags:', orderTags);
    } else {
      // Clear attribute if no add-on items are in cart
      attributes['Add-on Items'] = '';
      console.log('Cart upsell: Clearing cart attributes (no add-on items in cart)');
    }

    // Get current note to preserve it when updating attributes
    let userNote = '';
    const noteTextarea = document.querySelector('#CartDrawer-Note, #Cart-note');
    if (noteTextarea && noteTextarea.value !== undefined) {
      userNote = noteTextarea.value || '';
    } else {
      userNote = cart.note || '';
    }
    
    // Update cart attributes while preserving the note
    await this.updateCartAttributesRequest(attributes, userNote);
  }

  async updateCartAttributesFromCart() {
    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      if (!cartResponse.ok) {
        console.error('Failed to fetch cart for attribute update');
        return;
      }
      const cart = await cartResponse.json();
      await this.updateCartAttributes(cart);
    } catch (error) {
      console.error('Error updating cart attributes:', error);
    }
  }

  async updateCartAttributesRequest(attributes, note = '') {
    try {
      // Include note to preserve it when updating attributes
      // If note is not provided, get it from the cart note textarea if it exists
      let noteToPreserve = note;
      if (!noteToPreserve) {
        const noteTextarea = document.querySelector('#CartDrawer-Note, #Cart-note');
        if (noteTextarea) {
          noteToPreserve = noteTextarea.value || '';
        }
      }
      
      const body = JSON.stringify({ 
        attributes,
        note: noteToPreserve
      });
      
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{ body }
      });
      
      if (response.ok) {
        console.log('Cart upsell: Successfully updated cart attributes:', attributes);
      } else {
        console.error('Cart upsell: Failed to update cart attributes, status:', response.status);
      }
    } catch (error) {
      console.error('Cart upsell: Error updating cart attributes:', error);
    }
  }
}

customElements.define('cart-upsell', CartUpsell);

